import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendUp,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Circle,
  Warning
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

import { useLanguage } from '@/context/LanguageContext';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssessmentWizard = () => {
  const { companyId, assessmentId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { t, language } = useLanguage();

  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchData();
  }, [assessmentId]);

  const fetchData = async () => {
    try {
      // Fetch assessment details
      const assessmentRes = await axios.get(`${API_URL}/assessments/${assessmentId}`, {
        headers: getAuthHeaders()
      });
      setAssessment(assessmentRes.data);

      // Fetch categories with language
      const categoriesRes = await axios.get(`${API_URL}/categories`, {
        params: { lang: language },
        headers: getAuthHeaders()
      });
      setCategories(categoriesRes.data);

      // Fetch questions specific to this assessment's package with language
      const questionsRes = await axios.get(`${API_URL}/assessments/${assessmentId}/questions`, {
        params: { lang: language },
        headers: getAuthHeaders()
      });
      setQuestions(questionsRes.data);

      // Set current question index from assessment
      setCurrentQuestionIndex(assessmentRes.data.current_question_index || 0);
    } catch (error) {
      console.error('Failed to fetch assessment data:', error);
      toast.error('Failed to load assessment');
      navigate('/companies');
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  const handleAnswerSelect = (value, score) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: { value, score }
    });
  };

  // Persist answers to localStorage so a page refresh, tab crash or network
  // hiccup doesn't erase progress on a 79-question form.
  const STORAGE_KEY = `growthriq_answers_${assessmentId}`;
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') setAnswers((prev) => ({ ...parsed, ...prev }));
      }
    } catch (_) { /* ignore corrupt cache */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);
  useEffect(() => {
    try {
      if (Object.keys(answers).length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
      }
    } catch (_) { /* quota exceeded — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Resilient POST /answers with 3× retry + backoff. Returns true on success.
  const submitAnswerWithRetry = async (questionId, value, score) => {
    const payload = [{ question_id: questionId, answer: value, score }];
    const url = `${API_URL}/assessments/${assessmentId}/answers`;
    // Delays: 400ms, 1200ms, 2500ms
    const delays = [0, 400, 1200, 2500];
    let lastErr = null;
    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (delays[attempt] > 0) {
        await new Promise((r) => setTimeout(r, delays[attempt]));
      }
      try {
        await axios.post(url, payload, { headers: getAuthHeaders(), timeout: 15000 });
        return { ok: true };
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;
        // 4xx (except 408/429) are permanent — no point retrying
        if (status && status >= 400 && status < 500 && ![408, 429].includes(status)) {
          break;
        }
        // 5xx / network / timeout → continue retry loop
      }
    }
    return { ok: false, err: lastErr };
  };

  const humanizeAnswerError = (err) => {
    if (!err) return t('wizard.submit_error') || 'Cevap kaydedilemedi. Lütfen tekrar deneyin.';
    const status = err.response?.status;
    if (status === 401) return language === 'tr'
      ? 'Oturumunuz zaman aşımına uğradı. Cevaplarınız kaybolmadı — yeniden giriş yapıp devam edin.'
      : 'Your session expired. Your answers are safe — log in again to continue.';
    if (status === 404) return language === 'tr'
      ? 'Değerlendirme bulunamadı. Panele dönüp yeniden açın.'
      : 'Assessment not found. Return to dashboard and reopen.';
    if (status === 422) return language === 'tr'
      ? 'Cevap formatı sunucu tarafından reddedildi. Farklı bir seçenek deneyin.'
      : 'Answer format rejected by server. Try a different option.';
    if (status >= 500) return language === 'tr'
      ? 'Sunucu kısa süreli bir hata verdi. Cevaplarınız kayıp değil — "Tekrar Dene"yi tıklayın.'
      : 'Server had a brief error. Your answers are safe — click "Retry".';
    if (err.code === 'ECONNABORTED' || err.message?.includes('Network')) return language === 'tr'
      ? 'İnternet bağlantısı kesildi. Cevaplarınız yerelde saklandı; bağlanınca "Tekrar Dene"ye basın.'
      : 'Network dropped. Answers cached locally; click "Retry" when back online.';
    return language === 'tr' ? 'Cevap kaydedilemedi. "Tekrar Dene"ye basın.' : 'Failed to save. Click "Retry".';
  };

  const handleNext = async () => {
    if (!answers[currentQuestion?.id]) {
      toast.error(t('wizard.select_answer'));
      return;
    }

    const { value, score } = answers[currentQuestion.id];
    const qId = currentQuestion.id;

    // OPTIMISTIC UI: advance immediately so the user experiences an instant
    // question transition (no network wait between clicks). The POST is
    // fire-and-forget with the existing retry logic in the background.
    // Answers stay in localStorage as a safety net until the wizard exits,
    // and if the server ultimately rejects it we surface an actionable toast.
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }

    submitAnswerWithRetry(qId, value, score).then((result) => {
      if (result.ok) return;
      console.error('Failed to submit answer after retries:', result.err);
      toast.error(humanizeAnswerError(result.err), {
        duration: 15000,
        action: {
          label: language === 'tr' ? 'Tekrar Dene' : 'Retry',
          onClick: () => submitAnswerWithRetry(qId, value, score),
        },
      });
    });
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleComplete = async () => {
    if (!answers[currentQuestion?.id]) {
      toast.error(t('wizard.select_answer'));
      return;
    }

    setSubmitting(true);
    const { value, score } = answers[currentQuestion.id];
    // Step 1: Submit final answer (with retry — critical: don't lose the last answer)
    const submitResult = await submitAnswerWithRetry(currentQuestion.id, value, score);
    if (!submitResult.ok) {
      toast.error(humanizeAnswerError(submitResult.err), {
        duration: 15000,
        action: {
          label: language === 'tr' ? 'Tekrar Dene' : 'Retry',
          onClick: () => handleComplete(),
        },
      });
      setSubmitting(false);
      return;
    }

    try {
      // Step 2: Complete assessment (fast, <1s)
      await axios.post(`${API_URL}/assessments/${assessmentId}/complete`, {}, {
        headers: getAuthHeaders(),
        timeout: 15000,
      });

      toast.success(t('wizard.completed'));

      // Clean the localStorage cache for this assessment — form is done.
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }

      // Step 3: Fire-and-forget AI report generation (takes 30-90s).
      axios.post(
        `${API_URL}/assessments/${assessmentId}/generate-report?lang=${language}`,
        {},
        { headers: getAuthHeaders(), timeout: 240000 }
      ).catch((err) => {
        console.warn('Background report generation failed:', err);
      });

      // Step 4: Navigate immediately — user sees report shell, AI fields populate as they're ready
      navigate(`/report/${assessmentId}?generating=1`);
    } catch (error) {
      console.error('Failed to complete assessment:', error);
      const msg = error.response?.data?.detail || t('wizard.complete_error') || 'Failed to complete assessment';
      toast.error(msg, {
        duration: 12000,
        action: {
          label: language === 'tr' ? 'Tekrar Dene' : 'Retry',
          onClick: () => handleComplete(),
        },
      });
      setSubmitting(false);
    }
  };

  // Get current category progress
  const getCategoryProgress = () => {
    const categoryQuestions = {};
    questions.forEach((q, idx) => {
      if (!categoryQuestions[q.category_name]) {
        categoryQuestions[q.category_name] = { total: 0, answered: 0 };
      }
      categoryQuestions[q.category_name].total++;
      if (idx < currentQuestionIndex || answers[q.id]) {
        categoryQuestions[q.category_name].answered++;
      }
    });
    return categoryQuestions;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-[#52525B]">{t('wizard.loading')}</div>
      </div>
    );
  }

  const categoryProgress = getCategoryProgress();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-[#E4E4E7] sticky top-0 bg-white z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <Link to="/companies" className="flex items-center gap-2 text-[#52525B] hover:text-[#09090B]">
              <ArrowLeft size={20} />
              <span>{t('report.back')}</span>
            </Link>
            <div className="flex items-center gap-2">
              <Logo variant="light" size="md" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="label-text text-[#0F1B36]" data-testid="assessment-company">
                {assessment?.company_name}
              </p>
              <h1 className="heading-4 text-[#09090B]">
                {assessment?.assessment_type?.toUpperCase()} {t('wizard.title')}
              </h1>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#52525B]">{t('wizard.question', { current: currentQuestionIndex + 1, total: questions.length })}</p>
              <p className="text-2xl font-bold text-[#0F1B36]">{Math.round(progress)}%</p>
            </div>
          </div>
          <Progress value={progress} className="h-1 mt-4" />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar - Category Progress */}
        <div className="hidden lg:block">
          <h3 className="label-text text-[#52525B] mb-4">{language === 'tr' ? 'KATEGORİLER' : 'CATEGORIES'}</h3>
          <div className="space-y-3">
            {categories.map((cat) => {
              const prog = categoryProgress[cat.name];
              const isComplete = prog && prog.answered === prog.total;
              const isCurrent = currentQuestion?.category_name === cat.name;
              
              return (
                <div 
                  key={cat.id}
                  className={`flex items-center gap-2 text-sm ${
                    isCurrent ? 'text-[#0F1B36] font-medium' : 'text-[#52525B]'
                  }`}
                >
                  {isComplete ? (
                    <CheckCircle size={16} weight="fill" className="text-[#16A34A]" />
                  ) : (
                    <Circle size={16} className={isCurrent ? 'text-[#0F1B36]' : 'text-[#E4E4E7]'} />
                  )}
                  <span className="truncate">{cat.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Question Area */}
        <div className="lg:col-span-3">
          {currentQuestion && (
            <div className="animate-fade-in" data-testid="question-card">
              {/* Category Badge */}
              <div className="flex items-center gap-2 mb-6">
                <span className="label-text text-[#0F1B36] bg-[#0F1B36]/10 px-3 py-1">
                  {currentQuestion.category_name}
                </span>
                {currentQuestion.is_critical && (
                  <span className="label-text text-[#E60000] bg-[#E60000]/10 px-3 py-1 flex items-center gap-1">
                    <Warning size={14} />
                    {t('wizard.critical')}
                  </span>
                )}
              </div>

              {/* Question */}
              <h2 className="heading-3 text-[#09090B] mb-8" data-testid="question-text">
                {currentQuestion.text}
              </h2>

              {/* Options */}
              <RadioGroup
                value={answers[currentQuestion.id]?.value || ''}
                onValueChange={(value) => {
                  const option = currentQuestion.options.find(o => o.value === value);
                  handleAnswerSelect(value, option?.score || 0);
                }}
                className="space-y-4"
              >
                {currentQuestion.options.map((option, idx) => (
                  <div
                    key={option.value}
                    className={`flex items-center gap-4 p-4 border transition-all cursor-pointer ${
                      answers[currentQuestion.id]?.value === option.value
                        ? 'border-[#0F1B36] bg-[#0F1B36]/5'
                        : 'border-[#E4E4E7] hover:border-[#0F1B36]'
                    }`}
                    onClick={() => handleAnswerSelect(option.value, option.score)}
                    data-testid={`option-${idx}`}
                  >
                    <RadioGroupItem value={option.value} id={option.value} />
                    <Label 
                      htmlFor={option.value} 
                      className="flex-1 cursor-pointer text-[#09090B]"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-12 pt-8 border-t border-[#E4E4E7]">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                  className="rounded-none"
                  data-testid="prev-btn"
                >
                  <ArrowLeft size={16} className="mr-2" />
                  {t('wizard.previous')}
                </Button>

                {currentQuestionIndex === questions.length - 1 ? (
                  <Button
                    onClick={handleComplete}
                    disabled={submitting || !answers[currentQuestion?.id]}
                    className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-none"
                    data-testid="complete-btn"
                  >
                    {submitting ? t('wizard.submitting') : t('wizard.submit')}
                    <CheckCircle size={16} className="ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={!answers[currentQuestion?.id]}
                    className="bg-[#0F1B36] hover:bg-[#00227A] text-white rounded-none"
                    data-testid="next-btn"
                  >
                    {t('wizard.next')}
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentWizard;
